// src/components/TopBar.jsx
import React, { useState } from 'react';
import { Hospital, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { createPortal } from 'react-dom';
import { theme } from '../config/theme';

const LAST_PROFILE_KEY = 'musiq:last_profile_id';

/** Tri stable des profils : alphabétique nom + prénom */
function sortProfiles(profiles) {
  return [...(profiles || [])].sort((a, b) => {
    const na = `${a.profile_nom || ''} ${a.profile_prenom || ''}`.toLowerCase();
    const nb = `${b.profile_nom || ''} ${b.profile_prenom || ''}`.toLowerCase();
    return na.localeCompare(nb, 'fr');
  });
}

export default function TopBar() {
  const {
    user,
    profile,
    handleSetProfile: setProfile,
    etablissement,
    handleSetEtablissement: setEtablissement,
    handleSetChosenGroup: setChosenGroup,
    logout,
  } = useUser();
  const navigate = useNavigate();

  const [etabChoiceOpen, setEtabChoiceOpen] = useState(false);
  const [profileChoiceOpen, setProfileChoiceOpen] = useState(false);

  const etablissement_trigramme = etablissement ? etablissement['etablissement_trigramme'] : 'empty';

  const sortedProfiles = sortProfiles(user?.profiles);

  function handleChooseProfile(prfl) {
    setProfile(prfl);
    // Mémorise le dernier profil choisi pour pré-sélection à la reconnexion
    localStorage.setItem(LAST_PROFILE_KEY, prfl.profile_id);
    setProfileChoiceOpen(false);
  }

  return (
    <>
      {etabChoiceOpen &&
        createPortal(
          <div className="fixed top-0 left-0 w-full h-[100vh] z-[1002] flex items-center justify-center bg-black/60">
            <div
              className="absolute inset-0 bg-black/30"
              onClick={() => setEtabChoiceOpen(false)}
            />
            <div className="relative z-10 mx-3 rounded-2xl bg-white shadow-xl p-4 grid grid-cols-1 gap-2 w-[400px] max-w-[80vh]">
              <p className="text-xl">Où êtes-vous ?</p>
              {user.entities.map((ent) => (
                <div className="grid grid-cols-1 gap-1" key={'chng_ent_to_' + ent.entite_id}>
                  <p>{ent.entite_libelle}</p>
                  {ent.etablissements.map((etb) => (
                    <button
                      key={'chng_etb_to_' + etb.etablissement_id}
                      className={
                        'py-2 border-2 rounded border-red-800 ' +
                        (etb.etablissement_id === etablissement.etablissement_id
                          ? 'bg-red-600 text-white'
                          : 'bg-white text-red-600')
                      }
                      onClick={() => {
                        setEtablissement(etb);
                        setChosenGroup(null);
                        setEtabChoiceOpen(false);
                        navigate('/arrivee');
                      }}
                    >
                      {etb.etablissement_libelle}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </div>,
          document.body,
        )}

      {profileChoiceOpen &&
        createPortal(
          <div className="fixed top-0 left-0 w-full h-[100vh] z-[1002] flex items-center justify-center bg-black/60">
            <div
              className="absolute inset-0 bg-black/30"
              onClick={() => setProfileChoiceOpen(false)}
            />
            <div className="relative z-10 mx-3 rounded-2xl bg-white shadow-xl p-4 flex flex-col gap-2 w-[400px] max-w-[90vw] max-h-[80vh]">
              <p className="font-medium text-base shrink-0">Qui êtes-vous ?</p>
              <div className="flex flex-col gap-2 overflow-y-auto flex-1 pr-1">
                {sortedProfiles.map((prfl) => (
                  <button
                    key={'chng_prfl_to_' + prfl.profile_id}
                    className={
                      'py-2 border-2 rounded border-red-800 shrink-0 ' +
                      (prfl.profile_id === profile?.profile_id
                        ? 'bg-red-600 text-white'
                        : 'bg-white text-red-600')
                    }
                    onClick={() => handleChooseProfile(prfl)}
                  >
                    {prfl.profile_prenom + ' ' + prfl.profile_nom}
                  </button>
                ))}
              </div>
              <p className="text-right mt-1 shrink-0">
                <span className="cursor-pointer text-sm text-gray-500 hover:text-red-600" onClick={logout}>
                  Se déconnecter
                </span>
              </p>
            </div>
          </div>,
          document.body,
        )}

      {/* Boutons du bandeau supérieur */}
      <div className="fixed top-0 inset-x-0 z-[1001] max-w-screen-xl mx-auto w-full px-3 md:px-7 pt-2 text-[13px] font-semibold text-white p-2 flex justify-between items-center pointer-events-none">
        <button
          className={`min-h-[45px] text-left ${theme.topbarBg} ring-2 ${theme.topbarRing} rounded-3xl px-3 pointer-events-auto transition shadow-md`}
          onClick={() => setEtabChoiceOpen(true)}
        >
          <Hospital className="h-5 w-5 inline-block" />{' '}
          <span className="inline-block">{etablissement_trigramme}</span>
        </button>

        <button
          className={`min-h-[45px] text-right ${theme.topbarBg} ring-2 ${theme.topbarRing} rounded-3xl px-3 pointer-events-auto transition shadow-md`}
          onClick={() => setProfileChoiceOpen(true)}
        >
          <span className="inline-block">{profile ? profile.profile_pentagramme : ''}</span>{' '}
          <User className="h-5 w-5 inline-block" />
        </button>
      </div>
      <div style={{ height: 56 }} />
    </>
  );
}